/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/require-default-props */
import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../modules/auth/useAuth";
import { Modal, Button } from "@xfrontend/xui-react-plus";
import { toast } from "#/infra/Toastify";
import {
  UserItem, UserAssignment, addAssignment, removeAssignment,
} from "#/apis/infra/users";
import { LicenseItem } from "#/apis/infra/listLicenses";
import { CustomerItem } from "#/apis/infra/customers";
import RoleScopePicker, { RoleScopeValue } from "./RoleScopePicker";

const StyledModal = styled(Modal)`
  .modal-dialog { width: 560px; max-width: calc(100% - 32px); }
  .modal-content { background: var(--x-bg-500, #2d293a); border: 1px solid var(--x-purple-500, #494358); }
`;

const Section = styled.div`
  font-size: 12px; letter-spacing: .04em; text-transform: uppercase;
  color: var(--x-gray-500, #a9a9ac); margin: 4px 0 8px;
`;

// Identity header so it's always clear which user is being managed (username can be blank — fall
// back to email / id). Mirrors the table's user cell: bold name over a muted secondary line.
const UserHead = styled.div`
  margin: 0 0 16px; padding: 11px 13px;
  background: var(--x-purple-700, #2c2835); border: 1px solid var(--x-purple-500, #494358);
  border-radius: 8px;
  .label { font-size: 10.5px; letter-spacing: .05em; text-transform: uppercase; color: var(--x-gray-500, #a9a9ac); }
  .name { font-size: 14px; font-weight: 600; color: var(--x-gray-200, #f6f6f7); word-break: break-all; }
  .sub { font-size: 12px; color: var(--x-gray-500, #a9a9ac); word-break: break-all; }
`;

const SelfHint = styled.div`
  color: var(--x-gray-500, #a9a9ac); font-size: 12px; margin: -2px 0 8px;
`;

const Item = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  background: var(--x-purple-700, #2c2835); border: 1px solid var(--x-purple-500, #494358);
  border-radius: 8px; padding: 9px 12px; margin-bottom: 8px; font-size: 13px;
`;

const RoleTag = styled.span`
  font-size: 10.5px; font-weight: 700; color: var(--x-gray-300, #e9e9eb);
  background: var(--x-purple-500, #494358); border-radius: 20px; padding: 3px 8px; margin-right: 8px;
`;

const AddRow = styled.div`
  display: flex; align-items: flex-end; gap: 10px; margin-top: 12px;
  padding-top: 14px; border-top: 1px solid rgba(255,255,255,.06);
`;

type AssignmentModalProps = {
  show: boolean;
  user: UserItem | null;
  orgs: LicenseItem[];
  customers: CustomerItem[];
  scopeName: (scopeType: string, scopeId: string) => string;
  // True when the managed user is the signed-in user: removing/swapping your own roles is blocked
  // server-side (cannot_modify_self), so disable Remove and explain why.
  isSelf?: boolean;
  onClose: () => void;
  onChanged: () => void;
};

const AssignmentModal = ({
  show, user, orgs, customers, scopeName, isSelf = false, onClose, onChanged,
}: AssignmentModalProps): JSX.Element | null => {
  const { t } = useTranslation("management");
  const auth = useAuth();
  const [draft, setDraft] = useState<RoleScopeValue>({
    role: "ORG_USER", scope_type: "LICENSE", scope_id: orgs[0]?.license_id ?? "",
  });
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  const token = auth.user?.access_token;
  // username may be blank for some records — fall back so the header is never empty.
  const displayName = user.username || user.email || user.user_id;
  const hasSub = !!user.email && user.email !== displayName;

  const handleRemove = (a: UserAssignment): void => {
    if (!token || busy) return;
    setBusy(true);
    removeAssignment({
      token,
      userId: user.user_id,
      body: { role: a.role, scope_type: a.scope_type, scope_id: a.scope_id },
    }).then((res) => {
      setBusy(false);
      if (res.success) {
        toast.success(t("toast.roleRemoved"));
        onChanged();
      } else {
        toast.error(res.msg || t("toast.failed"));
      }
    });
  };

  const handleAdd = (): void => {
    if (!token || busy || !draft.scope_id) return;
    setBusy(true);
    addAssignment({
      token,
      userId: user.user_id,
      body: { role: draft.role, scope_type: draft.scope_type, scope_id: draft.scope_id },
    }).then((res) => {
      setBusy(false);
      if (res.success) {
        toast.success(t("toast.roleAdded"));
        onChanged();
      } else {
        toast.error(res.msg || t("toast.failed"));
      }
    });
  };

  return (
    <StyledModal
      title={`${t("assign.title")} — ${displayName}`}
      show={show}
      onClose={onClose}
      showCloseBtn
      restoreFocus={false}
    >
      <Modal.Body>
        <UserHead>
          <div className="label">{t("assign.managing")}</div>
          <div className="name">{displayName}</div>
          {hasSub && <div className="sub">{user.email}</div>}
        </UserHead>
        <Section>{t("assign.current")}</Section>
        {isSelf && <SelfHint>{t("assign.selfHint")}</SelfHint>}
        {user.assignments.length === 0 && (
          <div style={{ color: "var(--x-gray-500, #a9a9ac)", fontSize: 13 }}>{t("assign.none")}</div>
        )}
        {user.assignments.map((a) => (
          <Item key={`${a.role}:${a.scope_type}:${a.scope_id}`}>
            <span>
              <RoleTag>{a.role}</RoleTag>
              <span style={{ color: "var(--x-gray-400, #cfd0d3)" }}>
                {`@ ${scopeName(a.scope_type, a.scope_id)}`}
              </span>
            </span>
            <Button variant="secondary" size="sm" disabled={busy || isSelf} onClick={() => handleRemove(a)}>
              {t("assign.remove")}
            </Button>
          </Item>
        ))}

        <Section style={{ marginTop: 18 }}>{t("assign.add")}</Section>
        <AddRow>
          <div style={{ flex: 1 }}>
            <RoleScopePicker orgs={orgs} customers={customers} value={draft} onChange={setDraft} />
          </div>
          <Button variant="primary" disabled={busy || !draft.scope_id} onClick={handleAdd}>{t("assign.addBtn")}</Button>
        </AddRow>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t("assign.close")}</Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default AssignmentModal;
